import {
    namespace, Point, Line, Bounds,
} from '../../../../../FrontendBundle/Resources/js/ofcs';
import AbstractItem from './abstractItem';

namespace('Ofcs.Editor.Item');

/**
 * @class CutangleItem
 * @extends AbstractItem
 */
export default window.Ofcs.Editor.Item.CutangleItem = AbstractItem.extend({

    /**
     * This event is triggered when item is restored
     * by history redo/undo
     *
     * @event restore
     */

    type: 'cutangle',

    /**
     * Defines that an item can be moved
     * @property {boolean} supportsMove
     */
    supportsMove: false,

    /**
     * Defines that an item can be selected
     * amongst other items
     * @property {boolean} supportsMultiselection
     */
    supportsMultiselection: false,

    /**
     * Index denoting cut position in outlines
     * @property {number} index to outlines
     */
    cutIndex: null,

    /**
     * Enumerates all possible sealing options
     * @property {Array.<string>} sealingOptions
     */
    sealingOptions: ['without', 'with'],

    /**
     * Triggered when the segments are updated
     *
     * @event updatesegments
     * @param {CircleItem} item Me
     */

    /**
     * Triggered when handle points are clicked
     *
     * @event activatePoint
     * @param {Point} point - The coordinates of the clicked handle (start- or end-point)
     * @param {Number} angle - The angle of the clicked handle to the cut line
     * @param {Ofcs.Editor.Svg.Handle.ResizeCutangleHandle} handle - Instance of ResizeCutangleHandle
     */

    /**
     * Triggered when handle points are deactivated
     *
     * @event deactivatePoint
     * @param {Ofcs.Editor.Svg.Handle.ResizeCutangleHandle} handle - Instance of ResizeCutangleHandle
     */

    defaults: {
        /**
         * @property {String} Type
         */
        type: 'Cutangle',

        /**
         * Current cut line
         * @property {Line} attributes.line
         */
        line: new Line(new Point(0, 0), new Point(0, 0)),

        /**
         * current angle at cut's start point
         * @property {number}
         */
        startAngle: null,

        /**
         * current angle at cut's end point
         * @property {number}
         */
        endAngle: null,

        /**
         * Current position
         * @property {Point} attributes.position
         */
        position: new Point(0, 0),

        /**
         * Tells current tool status
         * default is "temporary" as long as cut has not been completed
         * will change to "set" once second click finalizes cut
         * @property {String} attributes.status
         */
        status: 'temporary',

        /**
         * Array with all outline segments for tabletop
         * @property {Line[]}
         */
        outlines: [],

        /**
         * Array with all outline vertices for tabletop
         * @property {Point[]}
         */
        outlinePath: [],

        /**
         * svg compliant polygon string
         * @property {String}
         */
        cutPath: null,

        /**
         * true if cut is in invalid state during creation
         * @property {boolean}
         */
        invalid: false,

        /**
         * Sealing
         * @property {String} attributes.sealing
         */
        sealing: 'without',

        /**
         * Denotes if item is currently blocked
         * Used when item is validated and reset after timeout
         * @property {boolean} attributes.blocked
         */
        blocked: false,

        /**
         * Defines it item is billable
         * Will be set and checked by tabletopCollisionManager
         * @property {boolean} attributes.billable
         */
        billable: true,

        /**
         * Current tool version - can be one of two:
         * '90' when angle is at 90°
         * 'custom' for all other cases
         * @property {String} attributes.toolVersion
         */
        toolVersion: null,
    },

    /**
     * @constructor
     */
    initialize() {
        AbstractItem.prototype.initialize.call(this);

        this.supportsWidth = true; // set to true for resize handles to work
        this.supportsHeight = false;
        this.supportsRotation = false;
        this.supportsPositioning = true;
        this.supportsDepth = false;
        this.supportsCollisions = false;
        this.supportsBidirectionalCollisions = false;
        this.supportsGuides = false;
        this.supportsSealing = true;
        this.supportsCopying = false;

        // Initialize paths
        this.set({
            cutPath: this.calculateCutPath(),
        }, { silent: true });

        this.on('change', this.onItemChange, this);
        this.on('delete', this.onItemRemove, this);
        this.on('restore', this.onRestore, this);
    },

    /**
     * Parses response data into attributes
     *
     * @param {Object} responseParam Response hash
     * @return {Object} Attribute hash
     */
    parse(responseParam) {
        const response = AbstractItem.prototype.parse.call(this, responseParam);
        let startPoint;
        let endPoint;

        if (response.startX !== undefined && response.startY !== undefined) {
            startPoint = new Point(response.startX, response.startY);

            delete response.startX;
            delete response.startY;
        }

        if (response.endX !== undefined && response.endY !== undefined) {
            endPoint = new Point(response.endX, response.endY);

            delete response.endX;
            delete response.endY;
        }

        if (startPoint !== undefined && endPoint !== undefined) {
            response.line = new Line(startPoint, endPoint);
        }

        if (response.version !== undefined) {
            response.status = 'set';
            response.toolVersion = response.version;

            delete response.version;
        }

        // remove position, which might have been added through the center config builder mechanism
        // position is not needed for cutangles.
        if (response.position) {
            delete response.position;
        }

        return response;
    },

    /**
     * Execute changes on item 'change' event
     *
     * @param {CutangleItem} item
     */
    onItemChange(item) {
        const changes = item.changed;

        if (changes.outlinePath !== undefined) {
            this.set('cutPath', this.calculateCutPath());
        }
    },

    onItemRemove() {
        if (this.get('status') !== 'temporary') {
            const { cutIndex } = this.parentItem;

            /**
             * TODO: connector: Refactor hack to 'segment manager' - canvas and selection is required for history
             * see: Ofcs.Editor.Svg.Handle.ResizeCutangleHandle
             * see: cutangleItem tests
             */
            this.handles.updateConnectors(this);

            // eslint-disable-next-line no-restricted-syntax
            for (const key in cutIndex) {
                if (cutIndex[key] && cutIndex[key].item === this) {
                    const newOutline = this.addCutSegments(key);
                    delete this.parentItem.cutIndex[key];

                    this.parentItem.updateCutIndex(
                        'remove',
                        this.parentItem.getOutlineSegments().length,
                        newOutline.length,
                        this.getOutlinePath(),
                        Number(key),
                    );

                    this.parentItem.setOutlineSegments(newOutline);
                    this.parentItem.setOutlinePath(this.generateOutlinePath(newOutline));

                    break;
                }
            }
        }
    },

    onRestore() {
        const helper = this.getHelper(this.parentItem);

        const sorted = helper.sortCutPoints(this.get('line').startPoint.clone(), this.get('line').endPoint.clone());
        helper.createCutSegments(sorted, this);
        helper.persist(this);
    },

    /**
     * @param {TabletopItem} tabletop
     * @return {Ofcs.Editor.Svg.Handle.CutangleHelper}
     */
    getHelper(tabletop) {
        return new Ofcs.Editor.Svg.Handle.CutangleHelper(tabletop);
    },

    /**
     * Generates svg complient polygon path from
     * outlinePath vertices
     *
     * @return {Array}
     */
    calculateCutPath() {
        const path = [];
        const outlinePath = this.get('outlinePath');

        for (let i = 0; i < outlinePath.length; i += 1) {
            if (i === 0) {
                path.push(['M', outlinePath[i].x, outlinePath[i].y]);
            } else {
                path.push(['L', outlinePath[i].x, outlinePath[i].y]);
            }
        }

        return [path];
    },

    /**
     * Returns a new bounding box based on the current *attributes* of the item
     *
     * @return {Bounds} Bounding box of the item
     */
    getBounds() {
        return this.cache.get('bounds', () => {
            const bounds = new Bounds(this.get('line').startPoint);
            Bounds.apply(bounds, this.getOutlinePath());

            return bounds;
        });
    },

    /**
     * Translate object into json stringable types. This method will be used
     * by JSON.stringify() automaticly.
     *
     * @return {Object}
     */
    toJSON(...args) {
        const attributes = AbstractItem.prototype.toJSON.apply(this, args);

        return {
            startX: attributes.line.startPoint.x,
            startY: attributes.line.startPoint.y,
            endX: attributes.line.endPoint.x,
            endY: attributes.line.endPoint.y,
            type: attributes.type,
            version: attributes.toolVersion,
            sealing: attributes.sealing,
            billable: attributes.billable,
        };
    },

    /**
     * Initializes item handles
     *
     * @param {Ofcs.Editor.Svg.Canvas} canvas - svg canvas instance
     * @return {Ofcs.Editor.Svg.Handle.ResizeCutangleHandle}
     */
    initializeHandles(canvas) {
        /**
         * TODO: Refactor hack to 'segment manager' - canvas and selection is required for history
         * see: Ofcs.Editor.Svg.Handle.ResizeCutangleHandle
         */
        this.handles = new Ofcs.Editor.Svg.Handle.ResizeCutangleHandle(this, canvas);

        return this.handles;
    },

    /**
     * Return safety margin for this item
     *
     * @return {number}
     */
    getSafetyMargin() {
        return 0;
    },

    /**
     * Returns all segments which currently make
     * up the cut item's outline path
     *
     * @return {Array}
     */
    getOutlineSegments() {
        return this.get('outlines');
    },

    /**
     * Sets new outline segments array
     *
     * @param {Line[]} outlines
     */
    setOutlineSegments(outlines) {
        this.set('outlines', outlines);
    },

    /**
     * Returns all vertices which currently make
     * up the cut item's outline path
     *
     * @return {Point[]}
     */
    getOutlinePath() {
        return this.get('outlinePath');
    },

    /**
     * Sets new outline path array
     *
     * @param {Point[]} path
     */
    setOutlinePath(path) {
        this.set('outlinePath', path);
    },

    /**
     * updates angle values
     *
     * @param {string} type - 'startAngle' or 'endAngle'
     * @param {number} value - actual degree value
     */
    updateCutAngle(type, value) {
        this.attributes[type] = value;

        if (value === 90) {
            this.set('toolVersion', '90');
        } else {
            this.set('toolVersion', 'custom');
        }
    },

    /**
     * Update start and end angle
     *
     * @param {number} startAngle
     * @param {number} endAngle
     */
    updateCutAngles(startAngle, endAngle) {
        this.set({
            startAngle,
            endAngle,
            toolVersion: startAngle === 90 ? '90' : 'custom',
        });
    },

    addCutSegments(index) {
        const tableSegments = this.parentItem.getOutlineSegments();
        const itemSegments = this.getOutlineSegments();
        const seg = {
            table: {
                before: tableSegments.slice(0, index),
                after: tableSegments.slice(Number(index) + 1),
            },

            item: {
                before: itemSegments.slice(0, this.cutIndex),
                after: itemSegments.slice(Number(this.cutIndex) + 1),
            },
        };
        let closing;
        let other;
        let result = [];

        // get closing area
        if (itemSegments.length - 1 === this.cutIndex) {
            closing = seg.item;
            other = seg.table;
        } else {
            closing = seg.table;
            other = seg.item;
        }

        // check segments before
        if (other.before.length === 0) {
            result = result.concat(closing.before);
        } else {
            if (other.before[other.before.length - 1].hasSameSlope(closing.before[0])) {
                const merge = new Line(
                    other.before[other.before.length - 1].startPoint.clone(),
                    closing.before[0].endPoint.clone(),
                );
                other.before.pop();
                closing.before[0] = merge;
            }
            result = result.concat(other.before, closing.before);
        }

        // check segments after
        if (other.after[0].hasSameSlope(closing.before[closing.before.length - 1])) {
            const merge = new Line(
                closing.before[closing.before.length - 1].startPoint.clone(),
                other.after[0].endPoint.clone(),
            );
            other.after[0] = merge;
            result.pop();
        }

        return result.concat(other.after);
    },

    generateOutlinePath(outline) {
        const result = [];

        for (let i = 0; i < outline.length; i += 1) {
            result.push(outline[i].startPoint.clone());
            if (i === outline.length - 1) {
                result.push(outline[i].endPoint.clone());
            }
        }

        return result;
    },

});
